import { service } from '@ember/service';
import Route from '@ember/routing/route';
import type EmployeeModel from 'tio-common/models/employee';
import type PslfFormModel from 'tio-common/models/pslf-form';
import type PslfProfileModel from 'tio-common/models/pslf-profile';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';

export default class AuthenticatedAdminPslfFormReviewRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;

  async model(params: { form_id: string }) {
    const { form_id } = params;

    const form = await this.store.findRecord<PslfFormModel>('pslf-form', form_id, {
      include: 'attachments,employee',
      reload: true,
    });

    const approver = this.sessionContext.currentEmployee;
    await approver.reload({
      include: 'pslf-profile,employee-addition',
    });

    let profile = approver.pslfProfile;
    if (!profile) {
      profile = this.store.createRecord<PslfProfileModel>('pslf-profile', {
        employee: approver,
      });
      await profile.save();
    }

    const [awaitingApproval, approved] = await Promise.all([
      this.store.query<PslfFormModel>('pslf-form', {
        filter: {
          'employee-id': form.employee.id,
          status: 'borrower_signed',
        },
        page: { limit: 5, offset: 0 },
        sort: '-created-at',
        reload: true,
      }),
      this.store.query<PslfFormModel>('pslf-form', {
        filter: {
          'employee-id': form.employee.id,
          status:
            'employer_signed,awaiting_submit,submitted,submit_errored,re_submit_errored,re_submitted',
        },
        page: { limit: 5, offset: 0 },
        sort: '-created-at',
        reload: true,
      }),
    ]);

    const formsAwaitingApproval = awaitingApproval.filter((f) => f.id !== form.id).slice();
    const formsApproved = approved.slice();

    return {
      form,
      formsAwaitingApproval,
      formsApproved,
      employee: approver,
    };
  }

  afterModel(model: { form: PslfFormModel; employee: EmployeeModel }) {
    if (model.form.status !== 'borrower_signed') {
      this.router.transitionTo('authenticated.admin.pslf');
    }
  }
}
